"use client";

import { useChat } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithToolCalls,
} from "ai";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import { unstable_serialize } from "swr/infinite";
import { BlockScoutSponsorModal } from "@/components/blockscout-sponsor-modal";
import { ChatHeader } from "@/components/chat-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAutoResume } from "@/hooks/use-auto-resume";
import { useChatVisibility } from "@/hooks/use-chat-visibility";
import { BLOCKSCOUT_RATE_LIMIT_ERROR_TYPE } from "@/lib/blockchain/types";
import type { Vote } from "@/lib/db/schema";
import { ChatSDKError } from "@/lib/errors";
import type { Attachment, ChatMessage } from "@/lib/types";
import type { AppUsage } from "@/lib/usage";
import { fetcher, fetchWithErrorHandlers, generateUUID } from "@/lib/utils";
import { Messages } from "./messages";
import { MultimodalInput } from "./multimodal-input";
import { getChatHistoryPaginationKey } from "./sidebar-history";
import { toast } from "./toast";
import type { VisibilityType } from "./visibility-selector";

/**
 * Check if a serialized tool output is a BlockScout rate limit error.
 * Works across server/client boundary by checking serialized properties.
 */
function isBlockScoutRateLimitError(output: unknown): boolean {
  if (!output || typeof output !== "object") {
    return false;
  }
  const obj = output as Record<string, unknown>;

  // Check by name (most reliable - we set this.name = "BlockScoutRateLimitError" in the error class)
  if (obj.name === "BlockScoutRateLimitError") {
    return true;
  }

  // Also check by context.errorType for extra reliability
  const context = obj.context as Record<string, unknown> | undefined;
  return context?.errorType === BLOCKSCOUT_RATE_LIMIT_ERROR_TYPE;
}

export function Chat({
  id,
  title,
  initialMessages,
  initialChatModel,
  initialVisibilityType,
  isReadonly,
  autoResume,
  initialLastContext,
}: {
  id: string;
  title: string;
  initialMessages: ChatMessage[];
  initialChatModel: string;
  initialVisibilityType: VisibilityType;
  isReadonly: boolean;
  autoResume: boolean;
  initialLastContext?: AppUsage;
}) {
  const { visibilityType } = useChatVisibility({
    chatId: id,
    initialVisibilityType,
  });

  const { mutate } = useSWRConfig();

  const [input, setInput] = useState<string>("");
  const [usage, setUsage] = useState<AppUsage | undefined>(initialLastContext);
  const [showCreditCardAlert, setShowCreditCardAlert] = useState(false);
  const [showBlockScoutSponsorModal, setShowBlockScoutSponsorModal] =
    useState(false);
  const [currentModelId, setCurrentModelId] = useState(initialChatModel);
  const currentModelIdRef = useRef(currentModelId);

  useEffect(() => {
    currentModelIdRef.current = currentModelId;
  }, [currentModelId]);

  const {
    messages,
    setMessages,
    sendMessage,
    status,
    stop,
    regenerate,
    resumeStream,
  } = useChat<ChatMessage>({
    id,
    messages: initialMessages,
    experimental_throttle: 100,
    generateId: generateUUID,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    transport: new DefaultChatTransport({
      api: "/api/chat",
      fetch: fetchWithErrorHandlers,

      prepareSendMessagesRequest(request) {
        const lastMessage = request.messages.at(-1);
        const isContinuation = lastMessage?.role === "assistant";

        // For continuation (after tool calls), we don't send the message
        // The backend will fetch messages from the database
        // This avoids schema validation issues with assistant message parts
        if (isContinuation) {
          return {
            body: {
              type: "continue" as const,
              id: request.id,
              selectedChatModel: currentModelIdRef.current,
              selectedVisibilityType: visibilityType,
            },
          };
        }

        // For user messages, send the full message for validation
        return {
          body: {
            type: "user-message" as const,
            id: request.id,
            message: lastMessage,
            selectedChatModel: currentModelIdRef.current,
            selectedVisibilityType: visibilityType,
          },
        };
      },
    }),
    onData: (dataPart) => {
      if (dataPart.type === "data-usage") {
        setUsage(dataPart.data);
      }
    },
    onFinish: () => {
      mutate(unstable_serialize(getChatHistoryPaginationKey));
    },
    onError: (error) => {
      if (error instanceof ChatSDKError) {
        // Check if it's a credit card error
        if (
          error.message?.includes("AI Gateway requires a valid credit card")
        ) {
          setShowCreditCardAlert(true);
        } else {
          toast({
            type: "error",
            description: error.message,
          });
        }
      }
    },
  });

  const searchParams = useSearchParams();
  const query = searchParams.get("query");

  const [hasAppendedQuery, setHasAppendedQuery] = useState(false);

  useEffect(() => {
    if (query && !hasAppendedQuery) {
      sendMessage({
        role: "user" as const,
        parts: [{ type: "text", text: query }],
      });

      setHasAppendedQuery(true);
      window.history.replaceState({}, "", `/chat/${id}`);
    }
  }, [query, sendMessage, hasAppendedQuery, id]);

  const { data: votes } = useSWR<Vote[]>(
    messages.length >= 2 ? `/api/vote?chatId=${id}` : null,
    fetcher
  );

  const [attachments, setAttachments] = useState<Attachment[]>([]);

  useAutoResume({
    autoResume,
    initialMessages,
    resumeStream,
  });

  // Track message IDs we've already shown the rate limit modal for
  const shownRateLimitForMessages = useRef<Set<string>>(new Set());

  // Detect BlockScout rate limit errors in tool outputs
  useEffect(() => {
    // Tool types that use BlockScout API
    const blockscoutToolTypes = [
      "tool-getTransactionsDetails",
      "tool-retrieveTransactions",
      "tool-getWalletState",
      "tool-getTokenStates",
    ];

    // Only check the most recent message for efficiency
    const lastMessage = messages.at(-1);
    if (!lastMessage || lastMessage.role !== "assistant") {
      return;
    }

    // Skip if we've already shown the modal for this message
    if (shownRateLimitForMessages.current.has(lastMessage.id)) {
      return;
    }

    // Scan tool parts for BlockScout rate limit errors
    for (const part of lastMessage.parts) {
      if (blockscoutToolTypes.includes(part.type)) {
        const toolPart = part as { output?: unknown };
        if (toolPart.output && isBlockScoutRateLimitError(toolPart.output)) {
          shownRateLimitForMessages.current.add(lastMessage.id);
          setShowBlockScoutSponsorModal(true);
          return;
        }
      }
    }
  }, [messages]);

  // Dev-only keyboard shortcut to trigger BlockScout sponsor modal (Ctrl+Shift+K)
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "K") {
        e.preventDefault();
        setShowBlockScoutSponsorModal(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <div className="overscroll-behavior-contain flex h-dvh min-w-0 touch-pan-y flex-col bg-background">
        <ChatHeader
          chatId={id}
          chatTitle={title}
          isReadonly={isReadonly}
          selectedVisibilityType={initialVisibilityType}
        />

        <Messages
          chatId={id}
          isReadonly={isReadonly}
          messages={messages}
          regenerate={regenerate}
          selectedModelId={initialChatModel}
          sendMessage={sendMessage}
          setInput={setInput}
          setMessages={setMessages}
          status={status}
          votes={votes}
        />

        <div className="sticky bottom-0 z-1 mx-auto flex w-full max-w-4xl gap-2 border-t-0 bg-background px-2 pb-3 md:px-4 md:pb-4">
          {!isReadonly && (
            <MultimodalInput
              attachments={attachments}
              chatId={id}
              input={input}
              messages={messages}
              onModelChange={setCurrentModelId}
              selectedModelId={currentModelId}
              selectedVisibilityType={visibilityType}
              sendMessage={sendMessage}
              setAttachments={setAttachments}
              setInput={setInput}
              setMessages={setMessages}
              status={status}
              stop={stop}
              usage={usage}
            />
          )}
        </div>
      </div>

      <AlertDialog
        onOpenChange={setShowCreditCardAlert}
        open={showCreditCardAlert}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate AI Gateway</AlertDialogTitle>
            <AlertDialogDescription>
              This application requires{" "}
              {process.env.NODE_ENV === "production" ? "the owner" : "you"} to
              activate Vercel AI Gateway.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                window.open(
                  "https://vercel.com/d?to=%2F%5Bteam%5D%2F%7E%2Fai%3Fmodal%3Dadd-credit-card",
                  "_blank"
                );
                window.location.href = "/";
              }}
            >
              Activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BlockScoutSponsorModal
        onOpenChange={setShowBlockScoutSponsorModal}
        open={showBlockScoutSponsorModal}
      />
    </>
  );
}
